import React, { useState, useEffect, useCallback } from "react";
import { Table, Card, Row, Col, Spinner, Alert, Badge, Form, Button } from "react-bootstrap";
import { FaChartBar, FaFileExcel, FaSync, FaChartLine } from "react-icons/fa";
import * as XLSX from "xlsx";
import firebaseApp from "../../../firebase/credenciales";
import { getFirestore, collection, query, where, getDocs } from "firebase/firestore";
import { periodoActual, toNum } from "../utils";

const db = getFirestore(firebaseApp);

const BTN = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };

const EstadosFinancieros = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [periodo, setPeriodo] = useState(periodoActual());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [balanceSheet, setBalanceSheet] = useState({ activos: {}, pasivos: {}, patrimonio: {} });
  const [pnl, setPnl] = useState({ ingresos: 0, gastos: 0, utilidad: 0 });

  const cargarEEFF = useCallback(async () => {
    if (!rucEmpresa) return;
    try {
      setLoading(true);
      setError("");

      const q = query(
        collection(db, "gco_asientos"),
        where("ruc_empresa", "==", rucEmpresa)
      );
      const snap = await getDocs(q);

      const debeByClass = {};
      const haberByClass = {};

      snap.docs.forEach(doc => {
        const a = doc.data();
        if (periodo && !String(a.fecha).startsWith(periodo)) return;

        (a.debe || []).forEach(l => {
          const clase = String(l.cuenta || "")[0] || "0";
          debeByClass[clase] = (debeByClass[clase] || 0) + toNum(l.monto);
        });

        (a.haber || []).forEach(l => {
          const clase = String(l.cuenta || "")[0] || "0";
          haberByClass[clase] = (haberByClass[clase] || 0) + toNum(l.monto);
        });
      });

      // Cálculo de P&L
      const ingresos = Math.max(0, (haberByClass["7"] || 0) - (debeByClass["7"] || 0));
      const gastos = Math.max(0, (debeByClass["6"] || 0) - (haberByClass["6"] || 0));
      const utilidad = ingresos - gastos;

      // Cálculo de Activos, Pasivos, Patrimonio
      const activos = Math.max(0, (debeByClass["1"] || 0) - (haberByClass["1"] || 0) + (debeByClass["2"] || 0) - (haberByClass["2"] || 0) + (debeByClass["3"] || 0) - (haberByClass["3"] || 0));
      const pasivos = Math.max(0, (haberByClass["4"] || 0) - (debeByClass["4"] || 0));
      const patrimonio = activos - pasivos;

      setPnl({ ingresos, gastos, utilidad });
      setBalanceSheet({
        activos: { total: activos },
        pasivos: { total: pasivos },
        patrimonio: { total: patrimonio }
      });

    } catch (e) {
      console.error(e);
      setError("No se pudieron generar los Estados Financieros.");
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa, periodo]);

  useEffect(() => {
    cargarEEFF();
  }, [cargarEEFF]);

  const actTotal = balanceSheet.activos.total || 0;
  const pasTotal = balanceSheet.pasivos.total || 0;
  const patTotal = balanceSheet.patrimonio.total || 0;
  
  const diferenciaCuadre = Number(Math.abs(actTotal - (pasTotal + patTotal)).toFixed(2));
  const cuadreCorrecto = diferenciaCuadre === 0;

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="border-0 shadow-sm">{error}</Alert>}

      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <Row className="align-items-center g-3">
            <Col md={1} className="text-center">
              <div className="rounded-3 p-3 d-inline-block bg-light text-secondary">
                <FaChartBar size={24} />
              </div>
            </Col>
            <Col md={5}>
              <h5 className="mb-1 fw-semibold">Estados Financieros</h5>
              <p className="text-muted mb-0 small">Estado de Resultados (P&L) y Situación Financiera Oficial.</p>
            </Col>
            <Col md={3}>
              <Form.Control type="month" className="fw-bold" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
            </Col>
            <Col md={3} className="text-end">
              <Button style={BTN} className="rounded-pill px-3 btn-sm" onClick={cargarEEFF} disabled={loading}><FaSync /> Actualizar</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Validación de la ecuación fundamental */}
      <div className="mb-4">
        {cuadreCorrecto ? (
          <Alert variant="success" className="d-flex align-items-center justify-content-between border-0 shadow-sm py-2 px-4" style={{ borderRadius: "12px" }}>
            <div className="fw-bold">✓ BALANCE DE SITUACIÓN CUADRADO</div>
            <div className="small opacity-75">Activo (S/ {actTotal.toFixed(2)}) = Pasivo + Patrimonio.</div>
          </Alert>
        ) : (
          <Alert variant="danger" className="d-flex align-items-center justify-content-between border-0 shadow-sm py-2 px-4" style={{ borderRadius: "12px" }}>
            <div className="fw-bold">🔴 ERROR DE CUADRE CONTABLE: S/ {diferenciaCuadre.toFixed(2)}</div>
            <div className="small opacity-75">Diferencia entre Activo y la suma de Pasivo + Patrimonio.</div>
          </Alert>
        )}
      </div>

      <Row className="g-4">
        {/* Estado de Resultados */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
            <Card.Header className="bg-white border-0 py-3 px-4">
              <h5 className="fw-bold mb-0 text-dark">Estado de Resultados (P&L)</h5>
            </Card.Header>
            <Card.Body className="px-4 pb-4">
              {loading ? <div className="text-center py-5"><Spinner animation="border" variant="secondary" /></div> : (
                <Table hover responsive className="mb-0">
                  <tbody>
                    <tr>
                      <td className="fw-semibold py-3">Ingresos Operacionales (Clase 7)</td>
                      <td className="text-end fw-bold text-success py-3">S/ {pnl.ingresos.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold py-3">Gastos Operativos (Clase 6)</td>
                      <td className="text-end fw-bold text-danger py-3">S/ {pnl.gastos.toFixed(2)}</td>
                    </tr>
                    <tr style={{ backgroundColor: "#f8f9fa" }}>
                      <td className="fw-bold py-3 text-uppercase">Resultado del Ejercicio (Utilidad)</td>
                      <td className="text-end fw-bold text-primary py-3">S/ {pnl.utilidad.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Estado de Situación Financiera */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
            <Card.Header className="bg-white border-0 py-3 px-4">
              <h5 className="fw-bold mb-0 text-dark">Estado de Situación Financiera</h5>
            </Card.Header>
            <Card.Body className="px-4 pb-4">
              {loading ? <div className="text-center py-5"><Spinner animation="border" variant="secondary" /></div> : (
                <Table hover responsive className="mb-0">
                  <tbody>
                    <tr>
                      <td className="fw-bold text-uppercase py-3">ACTIVOS TOTALES (Clase 1, 2, 3)</td>
                      <td className="text-end fw-bold text-success py-3">S/ {actTotal.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold text-uppercase py-3">PASIVOS TOTALES (Clase 4)</td>
                      <td className="text-end fw-bold text-danger py-3">S/ {pasTotal.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold text-uppercase py-3">PATRIMONIO NETO (Activo - Pasivo)</td>
                      <td className="text-end fw-bold text-primary py-3">S/ {patTotal.toFixed(2)}</td>
                    </tr>
                    <tr style={{ backgroundColor: "#f8f9fa", borderTop: "2px solid #dee2e6" }}>
                      <td className="fw-bold text-uppercase py-3">Total Pasivo + Patrimonio</td>
                      <td className="text-end fw-bold text-dark py-3">S/ {(pasTotal + patTotal).toFixed(2)}</td>
                    </tr>
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EstadosFinancieros;
